import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { facultiesApi } from "../../services/nomenclatures/facultiesApi";
import { useRef, useState } from "react";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { specialityModesApi } from "../../services/nomenclatures/specialityModesApi";
import { studentStatusesApi } from "../../services/nomenclatures/studentStatusesApi";
import CustomInputText from "../../components/forms/CustomInputText";
import { Button } from "primereact/button";
import { useFormikContext } from "formik";
import { StudentInfoAdminResponse } from "../../models/dtos";
import { ApiResponse } from "../../services/base/apiResponse";
import { admissionReasonsApi } from "../../services/nomenclatures/admissionReasonsApi";
import { degreesApi } from "../../services/nomenclatures/degreesApi";
import { groupsApi } from "../../services/nomenclatures/groupsApi";
import CustomDropdown from "../../components/forms/CustomDropdown";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { Link } from "react-router-dom";
import ItemBrowser from "../../components/forms/ItemBrowser";
import { plansApi } from "../../services/plans/plansApi";
import { specialityTypesApi } from "../../services/nomenclatures/specialityTypesApi";
import { specialityFeesApi } from "../../services/nomenclatures/specialityFeesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import CustomCheckbox from "../../components/forms/CustomCheckbox";
import { masterDegreeTypesApi } from "../../services/nomenclatures/masterDegreeTypesApi";
import { createEditDialog as createGroupDialog } from "../nomenclatures/Groups";
import { studentsApi } from "../../services/students/studentsApi";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Accordion, AccordionTab } from "primereact/accordion";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import FilePreviewDialog from "../../components/common/FilePreviewDialog";
import StudentHistorySection from "./StudentHistorySection";

interface StudentDataProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const currentYear = new Date().getFullYear();

const StudentData = ({ student }: StudentDataProps) => {
  const { data: faculties } = facultiesApi.useGetFacultiesQuery();
  const { data: specialities } = specialitiesApi.useGetSpecialitiesQuery();
  const { data: specialityTypes } = specialityTypesApi.useGetSpecialityTypesQuery();
  const { data: eduForm } = specialityModesApi.useGetSpecialityModesQuery();
  const { data: studentStatus } = studentStatusesApi.useGetStudentStatusesQuery();
  const { data: admissionReason } = admissionReasonsApi.useGetAdmissionReasonsQuery();
  const { data: degrees } = degreesApi.useGetDegreesQuery();
  const { data: groups } = groupsApi.useGetGroupsQuery();
  const { data: specialityFees } = specialityFeesApi.useGetSpecialityFeesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const { data: masterDegreeTypes } = masterDegreeTypesApi.useGetMasterDegreeTypesQuery();

  const admissionSemesterTypes = [
    { value: 1, label: "зимен семестър" },
    { value: 2, label: "летен семестър" },
  ];

  const createGroupMutation = groupsApi.useUpdateGroupMutation();
  //const { data: planTypes } = planTypesApi.useGetPlanTypesQuery();
  const [triggerPlansQuery] = plansApi.useLazyGetPlansByFacSpecQuery();
  const [triggerFetchUanQuery, { error: fetchUanError }] = studentsApi.useLazyFetchUanFromNacidQuery();

  const { data: studentNacidEvents } = studentsApi.useGetStudentNacidEventsQuery(student?.data.studentId!);

  const [uploadRelocationFile, { isLoading: isUploadingRelocationFile }] =
    studentsApi.useUploadRelocationFileMutation();
  const [uploadDiploma, { isLoading: isUploadingDiploma }] = studentsApi.useUploadDiplomaMutation();
  const [sendDiplomaDataToNacid, { isLoading: isSendingDiplomaDataToNacid }] =
    studentsApi.useSendDiplomaDataToNacidMutation();

  const [triggerDownloadRelocationFile, { isLoading: isDownloadingRelocationFile }] =
    studentsApi.useLazyDownloadRelocationFileQuery();
  const [triggerDownloadDiploma, { isLoading: isDownloadingDiploma }] = studentsApi.useLazyDownloadDiplomaQuery();
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const toast = useRef<Toast>(null);

  const formik = useFormikContext<any>();

  const handleFetchUan = async () => {
    const nacidResult = await triggerFetchUanQuery(student?.data.studentId!).unwrap();
    nacidResult.data.uan && formik.setFieldValue("uan", nacidResult.data.uan);
  };

  const handleDownloadRelocationFile = async (studentId: number) => {
    const result = (await triggerDownloadRelocationFile(studentId).unwrap()) as any;
    console.log("Downloaded Relocation File:", result);
    openPdfInModal(result);
  };

  const handleDownloadDiploma = async (studentId: number) => {
    const result = (await triggerDownloadDiploma(studentId).unwrap()) as any;
    console.log("Downloaded Diploma:", result);
    openPdfInModal(result);
  };

  const openPdfInModal = async (result: any) => {
    try {
      const url = URL.createObjectURL(result.blob as Blob);
      setPdfUrl(url);
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading relocation file:", error);
      toast.current!.show({
        severity: "error",
        summary: "Грешка",
        detail: "Възникна грешка при изтеглянето на файла за преместване.",
      });
    }
  };

  const closePdfDialog = () => {
    setShowPdfDialog(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const onUploadRelocationFile = (event: FileUploadHandlerEvent) => {
    uploadRelocationFile({ studentId: student?.data.studentId!, relocationFile: event.files[0] }).then((response) => {
      if ("data" in response && response.data) {
        toast.current!.show({ severity: "success", summary: "Успешно", detail: "Файлът за преместване е качен." });
      } else {
        toast.current!.show({
          severity: "error",
          summary: "Грешка",
          detail: "Файлът за преместване не е качен, моля проверете файла.",
        });
      }
    });
  };

  const onUploadDiploma = (event: FileUploadHandlerEvent) => {
    uploadDiploma({ studentId: student?.data.studentId!, diploma: event.files[0] }).then((response) => {
      if ("data" in response && response.data) {
        toast.current!.show({ severity: "success", summary: "Успешно", detail: "Дипломата е качена." });
      } else {
        toast.current!.show({
          severity: "error",
          summary: "Грешка",
          detail: "Дипломата не е качена, моля проверете файла.",
        });
      }
    });
  };

  const handleSendDiplomaDataToNacid = async (studentId: number) => {
    sendDiplomaDataToNacid(studentId).then((response) => {
      if ("data" in response && response.data) {
        toast.current!.show({
          severity: "success",
          summary: "Успешно",
          detail: "Данните за дипломата са изпратени към НАЦИД.",
        });
      } else {
        toast.current!.show({
          severity: "error",
          summary: "Грешка",
          detail: "Възникна грешка при изпращането на данните за дипломата към НАЦИД.",
        });
      }
    });
  };

  return (
    <div className="flex justify-content-center flex-wrap">
      <Card className="w-full mt-4">
        <Divider />
        <div className="col-12">
          <div className="card">
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <h4>НАЦИД</h4>
              </div>
              <div className="field col-12 md:col-4">
                <div className="flex flex-row align-items-center">
                  <div className="flex-1">
                    <CustomInputText id="uan" label="ЕАН" />
                    {fetchUanError && <small className="text-red-500">Неуспешно извличане на ЕАН.</small>}
                  </div>
                  {student?.data.uan && (
                    <a
                      href={`https://regstudents.nacid.bg/personLot/${student?.data.uan}/Student`}
                      target="_blank"
                      className="text-blue-500 hover:text-blue-700 ml-2"
                    >
                      <i className="pi pi-external-link"></i>
                    </a>
                  )}
                  <Button icon="pi pi-download" link onClick={handleFetchUan} />
                </div>
              </div>
              <div className="field col-12 md:col-4">
                <CustomInputText id="und" label="ЕНД" />
              </div>
              <div className="field col-12">
                <Accordion>
                  <AccordionTab header="НАЦИД Операции">
                    <div className="field col-12">
                      <DataTable
                        value={studentNacidEvents?.data ?? []}
                        emptyMessage="Не е открита допълнителна информация за операциите с НАЦИД."
                        stripedRows
                        paginator
                        sortField="date"
                        sortOrder={1}
                        rows={5}
                      >
                        <Column field="date" header="Дата" />
                        <Column field="nacidEventName" header="Събитие" />
                        <Column field="error" header="Грешка" />
                        <Column field="errorExplanation" header="Обяснение за грешка" />
                        <Column
                          field="requestObject"
                          header="Заявка"
                          body={(rowData) => (
                            <ul>
                              {rowData.requestObject &&
                                Object.entries(rowData.requestObject).map(([key, value]) => (
                                  <li key={key}>
                                    <strong>{key}:</strong> {value as string}
                                  </li>
                                ))}
                            </ul>
                          )}
                        />
                        <Column
                          field="responseObject"
                          header="Отговор"
                          body={(rowData) => (
                            <ul>
                              {rowData.responseObject &&
                                Object.entries(rowData.responseObject).map(([key, value]) => (
                                  <li key={key}>
                                    <strong>{key}:</strong> {value as string}
                                  </li>
                                ))}
                            </ul>
                          )}
                        />
                      </DataTable>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>

              <Divider />
              <div className="col-12">
                <h4>Основни студентски данни</h4>
              </div>

              {student?.data?.studentId && (
                <div className="field col-12 md:col-12">
                  <CustomInputText id="facultyNumber" label="Факултетен номер" required />
                </div>
              )}

              <div className="field col-12 md:col-8">
                <CustomDropdown
                  id="admissionReasonId"
                  filter
                  options={admissionReason?.data ?? []}
                  optionLabel={(option) => `${option.nacidCode} // ${option.name.substring(0, 130)}`}
                  optionValue="admissionReasonId"
                  placeholder="Изберете опция"
                  label="Основание за прием"
                  labelIcon="pi pi-exclamation-triangle"
                  required
                  showClear
                />
              </div>
              <div className="field col-12 md:col-4">
                <CustomInputText id="specialityOrder" label="Заповед за прием" />
              </div>

              {[71, 77, 78].includes(formik.values.admissionReasonId) && (
                <>
                  <div className="field col-12 md:col-4">
                    <CustomInputText id="relocationNumber" label="Номер на преместване" />
                  </div>
                  <div className="field col-12 md:col-4">
                    <CustomDatepicker id="relocationDate" label="Дата на преместване" />
                  </div>
                  <div className="field col-12 md:col-4 flex flex-row">
                    <div className="flex flex-column gap-2 mb-2">
                      <label>Файл за преместване</label>
                      <FileUpload
                        mode="basic"
                        name="relocationFile"
                        accept="image/*,application/pdf"
                        maxFileSize={5000000}
                        customUpload
                        auto
                        chooseLabel={isUploadingRelocationFile ? "Качване..." : "Качи файл"}
                        uploadHandler={(event) => {
                          if (onUploadRelocationFile) {
                            onUploadRelocationFile(event);
                          }
                        }}
                        chooseOptions={{
                          className: "p-button-outlined p-button-secondary",
                          icon: isUploadingRelocationFile ? "pi pi-spinner pi-spin" : "pi pi-upload",
                        }}
                      />
                    </div>
                    {formik.values.hasRelocationFile && (
                      <div className="flex text-gray-500 text-sm">
                        <Button
                          link
                          onClick={() => handleDownloadRelocationFile(student?.data.studentId!)}
                          loading={isDownloadingRelocationFile}
                        >
                          <i className="pi pi-download mr-2"></i>
                          Преглед
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="admissionAcademicYearId"
                  options={academicYears?.data ?? []}
                  optionLabel="name"
                  optionValue="academicYearId"
                  placeholder="Изберете академична година"
                  label="Академична година"
                  defaultValue={(options) => options.find((x) => x.yearNumber === currentYear)}
                  required
                />
              </div>
              <div className="field col-12 md:col-2">
                <label htmlFor="admissionSemesterType">Семестър на прием</label>
                <Dropdown
                  id="admissionSemesterType"
                  name="admissionSemesterType"
                  value={formik.values.admissionSemesterType || 1}
                  options={admissionSemesterTypes}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Изберете опция"
                  onChange={(e) => formik.setFieldValue("admissionSemesterType", e.value)}
                  className="w-full"
                />
              </div>
              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="specialityModeId"
                  options={eduForm?.data ?? []}
                  optionLabel="name"
                  optionValue="specialityModeId"
                  placeholder="Изберете опция"
                  label="Форма на обучение"
                  required
                />
              </div>

              <div className="field col-12 md:col-4">
                <CustomDropdown
                  id="facultyId"
                  options={faculties?.data ?? []}
                  optionLabel="name"
                  optionValue="facultyId"
                  placeholder="Изберете опция"
                  label="Факултет"
                  required
                />
              </div>

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="degreeId"
                  options={degrees?.data ?? []}
                  optionLabel="name"
                  optionValue="degreeId"
                  placeholder="Изберете ОКС"
                  label="ОКС"
                  required
                />
              </div>

              <div className="field col-12 md:col-1">
                <CustomInputText id="semester" type="text" label="Семестър" required />
              </div>

              <div className="field col-12 md:col-8">
                <CustomDropdown
                  id="specialityId"
                  options={specialities?.data ?? []}
                  optionLabel="name"
                  optionValue="specialityId"
                  filterValue="facultyId"
                  filterExpression={(s) =>
                    s.facultyId === formik.values.facultyId &&
                    s.degreeId === formik.values.degreeId &&
                    s.specialityModeId === formik.values.specialityModeId
                  }
                  placeholder="Изберете специалност"
                  label="Специалност"
                  required
                />
              </div>
              <div className="field col-12 md:col-2">
                <CustomInputText id="universityFieldName" type="text" disabled label="Професионално направление" />
              </div>

              <div className="field col-12 md:col-2 flex align-items-center">
                <CustomCheckbox id="isStudyingInEnglish" label="Обучение на английски език" />
              </div>

              {formik.values.degreeId === 2 && (
                <div className="field col-12 md:col-2">
                  <CustomDropdown
                    id="masterDegreeTypeId"
                    label="Тип магистър"
                    options={masterDegreeTypes?.data ?? []}
                    optionLabel="name"
                    optionValue="masterDegreeTypeId"
                    placeholder="Изберете опция"
                    required={formik.values.degreeId === 2}
                  />
                </div>
              )}

              <div className="field col-12 md:col-2">
                <CustomDropdown
                  id="specialityTypeId"
                  options={specialityTypes?.data ?? []}
                  optionLabel="name"
                  optionValue="specialityTypeId"
                  placeholder="Изберете опция"
                  label="Тип финансиране"
                  required
                />
              </div>
              <div className="field col-12 md:col-6">
                <CustomDropdown
                  id="specialityFeeId"
                  options={specialityFees?.data ?? []}
                  optionLabel={(option) =>
                    `${option.specialityName} - ${option.feeTypeName} - ${option.academicYearName} ${option.isForeignersFee ? " - чужденци" : ""} ${option.isOutsideEu ? " - извън ЕС" : ""} ${option.isEnglishFee ? " - обучение на АЕ" : ""} - ${option.feeAmount} ${option.currencySymbol ?? option.currencyCode}`
                  }
                  optionValue="specialityFeeId"
                  filterExpression={(f) =>
                    f.specialityId === formik.values.specialityId ||
                    f.childrenSpecialityIds?.includes(formik.values.specialityId)
                  }
                  placeholder="Изберете опция"
                  label="Такса"
                  required
                />
              </div>

              <div className="field col-12 md:col-2 flex align-items-center">
                <CustomCheckbox id="hasContract" label="Има договор" />
              </div>

              <div className="field col-12 md:col-1">
                <CustomInputText id="mainBookNumber" label="Главна книга №" />
              </div>
              <div className="field col-12 md:col-1">
                <CustomInputText id="mainBookPage" label="Страница гл.кн." />
              </div>

              <Divider />

              <div className="field col-12 md:col-4">
                <CustomDropdown
                  id="groupId"
                  labelIcon="pi pi-exclamation-triangle"
                  options={groups?.data ?? []}
                  optionLabel={(g) => `${g.name} - ${g.academicYearName} - ${g.studentsCount} бр.`}
                  optionValue="groupId"
                  filterExpression={(g) =>
                    g.specialityId === formik.values.specialityId &&
                    g.academicYearId === formik.values.admissionAcademicYearId
                  }
                  placeholder="Изберете опция"
                  label="Група"
                  createDialog={createGroupDialog(specialities, academicYears)}
                  createItemMutation={createGroupMutation}
                  showClear
                />
              </div>
              <div className="field col-12 md:col-1">
                <CustomInputText id="subgroup" type="text" label="Подгрупа" />
              </div>

              <Divider />
              <div className="col-12">
                <h4>Диплома</h4>
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="diplomaSeries" label="Серия на дипломата" />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="diplomaSerialNumber" label="Сериен номер на дипломата" />
              </div>
              <div className="field col-12 md:col-3">
                <CustomInputText id="diplomaRegistrationNumber" label="Регистрационен номер на дипломата" />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDatepicker id="diplomaDate" label="Дата на дипломата" />
              </div>
              <div className="field col-12 md:col-6 flex flex-row">
                <div className="flex flex-column gap-2 mb-2">
                  <label>Диплома (сканирана, PDF формат)</label>
                  <div className="flex flex-row justify-content-between align-items-center gap-2 mb-2">
                    <FileUpload
                      mode="basic"
                      name="diplomaFile"
                      accept="image/*,application/pdf"
                      maxFileSize={5000000}
                      customUpload
                      auto
                      chooseLabel={isUploadingDiploma ? "Качване..." : "Качи файл"}
                      uploadHandler={(event) => {
                        if (onUploadDiploma) {
                          onUploadDiploma(event);
                        }
                      }}
                      chooseOptions={{
                        className: "p-button-outlined p-button-secondary",
                        icon: isUploadingDiploma ? "pi pi-spinner pi-spin" : "pi pi-upload",
                      }}
                    />
                    {formik.values.hasDiplomaFile && (
                      <div className="flex flex-row gap-2 mb-2">
                        <Button
                          link
                          onClick={() => handleDownloadDiploma(student?.data.studentId!)}
                          loading={isDownloadingDiploma}
                        >
                          <i className="pi pi-download mr-2"></i>
                          Преглед
                        </Button>
                        <Button
                          onClick={() => handleSendDiplomaDataToNacid(student?.data.studentId!)}
                          loading={isSendingDiplomaDataToNacid}
                          label="Изпрати диплома към НАЦИД"
                          icon="pi pi-send"
                          outlined
                          severity="secondary"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Divider />
              <div className="col-12">
                <h4>Учебен план</h4>
              </div>
              {student?.data?.studentId && (
                <>
                  <div className="field col-12 md:col-3">
                    <CustomInputText id="planTypeName" type="text" disabled label="Тип" />
                  </div>
                  <div className="field col-12 md:col-3">
                    <CustomInputText id="planCode" type="text" disabled label="Код" />
                  </div>
                  <div className="field col-12 md:col-4">
                    <CustomInputText id="academicPlanInfo" type="text" disabled label="Период на обучение" />
                  </div>
                  <div className="field col-12 md:col-2 flex align-items-end">
                    <Link to={`/plans/${student?.data.planId}`}>
                      <Button label="Преглед" icon="pi pi-eye" outlined></Button>
                    </Link>
                  </div>
                </>
              )}
              <div className="field col-12">
                <ItemBrowser
                  id="planId"
                  label="Учебен план"
                  optionsPromise={() =>
                    triggerPlansQuery({
                      facultyId: formik.values.facultyId,
                      specialityId: formik.values.specialityId,
                    })
                  }
                  idField="planId"
                  labelField="code"
                  currentLabelText={formik?.values.planCode}
                  columns={[
                    { field: "planId", header: "ID", sortable: true, filter: true },
                    { field: "code", header: "Код", sortable: true, filter: true },
                    {
                      field: "startAcademicYearYearNumber",
                      header: "Начална академична година",
                      sortable: true,
                      filter: true,
                    },
                    {
                      field: "endAcademicYearYearNumber",
                      header: "Крайна академична година",
                      sortable: true,
                      filter: true,
                    },
                    { field: "specialityName", header: "Специалност", sortable: true, filter: true },
                    { field: "degreeName", header: "Степен", sortable: true, filter: true },
                    { field: "planTypeName", header: "Тип", sortable: true, filter: true },
                    { field: "specialityModeName", header: "Форма на обучение", sortable: true, filter: true },
                  ]}
                />
              </div>

              <Divider />

              <div className="col-12">
                <h4>Статус на студента</h4>
              </div>
              <div className="field col-12 md:col-5">
                <CustomDropdown
                  id="studentStatusId"
                  label="Статус"
                  labelIcon="pi pi-exclamation-triangle"
                  options={studentStatus?.data ?? []}
                  optionLabel="name"
                  optionValue="studentStatusId"
                  placeholder="Изберете опция"
                  required
                />
              </div>
              <div className="field col-12 md:col-3">
                <CustomDatepicker id="studentModifiedStatusDate" disabled label="Дата промяна на статус" />
              </div>

              {student?.data?.studentId && (
                <StudentHistorySection
                  studentId={student.data.studentId}
                  studentHistories={student.data.studentHistories || []}
                />
              )}
            </div>
          </div>
        </div>
      </Card>

      <FilePreviewDialog visible={showPdfDialog} onHide={closePdfDialog} fileUrl={pdfUrl} title="Файл" />
    </div>
  );
};

export default StudentData;
